import {
  changedFrontDeskItems,
  frontDeskAccessOf,
  frontDeskClientItems,
  frontDeskItems,
  frontDeskPageItems,
  type FrontDeskAccess,
  type FrontDeskItem,
} from "@teamna/shared";
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import {
  Avatar,
  Button,
  Checkbox,
  ErrorState,
  FormSection,
  InlineError,
  ShellNavPreview,
  SkeletonList,
  StatTile,
  showToast,
  type ShellNavItem,
} from "../components/index.ts";
import { useLabels } from "@teamna/web-client/hooks/use-labels.ts";
import { useOwnerBusiness } from "@teamna/web-client/hooks/use-owner.ts";
import { useShape } from "@teamna/web-client/hooks/use-shape.ts";
import { useUpdateBusiness } from "@teamna/web-client/hooks/use-setup.ts";
import { FRONT_DESK_ITEM_PAGES, ownerNavGroups } from "./nav.ts";
import { SaveBar } from "./setup/frame.tsx";

/**
 * Settings, Front desk (owner and admin; decisions D58, D65, D68 of the Pilates studio shape).
 *
 * The owner ticks what the Front desk role may see and do: the pages of the office, and the jobs on
 * a client. Each tick has one line that says what it gives. Next to the list (under it on a phone),
 * the preview draws the front desk's screen from the ticks as they are now, not as saved: its menu
 * (the real menu, `ownerNavGroups` with the ticks) and a client page. A part that comes or goes has
 * a short highlight, so the owner sees what each tick changes. Save sends the items that changed
 * (`settings.frontDesk`); the API refuses an item that is off (`frontDeskRules`).
 */

type Words = ReturnType<typeof itemWords>;

/** The title and the line of each item, in the business's own words. */
function itemWords(label: ReturnType<typeof useLabels>["label"]) {
  const members = label("member", { plural: true });
  const member = label("member");
  const groups = label("group", { plural: true });
  const Groups = label("group", { plural: true, capital: true });
  return {
    homeNumbers: { title: "Home numbers", hint: `Sees the numbers on Home: money, renewals, ${members} who left, and open places.` },
    classes: { title: Groups, hint: `Sees the ${Groups} page: each ${label("group")}, its times and its sessions. Cannot change them.` },
    classesEdit: {
      title: `Change ${groups}`,
      hint: `Adds, changes and deletes ${groups}: the places, the ${label("staff", { plural: true })} and the booking rules. Needs ${Groups}.`,
    },
    payments: { title: "Payments", hint: "Sees the Payments page: each payment to the business." },
    team: { title: "Team", hint: "Sees the Staff page: the names, the roles and the mobile numbers. Cannot change the team." },
    adjustBalance: { title: "Add or remove sessions", hint: `Changes the sessions of a ${member} by hand.` },
    freeze: { title: "Freeze a package", hint: "Freezes a package and ends a freeze." },
    paymentRequests: { title: "Ask for an amount", hint: "Sends a request to pay, with a pay link, and cancels a request." },
    photo: { title: "Profile photos", hint: `Adds or changes the photo of a ${label("participant")}. Never a photo of the team.` },
    editClient: { title: `Change the details of a ${member}`, hint: `Opens the Settings tab of a ${member}: names, numbers, ${label("group")}, hold and remove.` },
    addClients: { title: `Add ${members}`, hint: `Adds new ${members} to the list.` },
    sendInvites: { title: "Send invites", hint: `Sends the invites to join the Teamna app.` },
    messageClients: { title: `Message many ${members}`, hint: `Sends one message to many ${members}, and the renewals and reminders on Home.` },
    exportClients: { title: "Export", hint: `Downloads the list of ${members} as a file.` },
  } satisfies Record<FrontDeskItem, { title: string; hint: string }>;
}

export function FrontDeskSettings({ businessId }: { businessId: string }) {
  const business = useOwnerBusiness(businessId);
  if (business.isLoading) return <SkeletonList rows={4} />;
  if (business.error || !business.data) {
    return <ErrorState error={business.error ?? new Error("missing")} onRetry={() => void business.refetch()} />;
  }
  const saved = frontDeskAccessOf(business.data.settings.frontDesk);
  return <FrontDeskForm key={JSON.stringify(saved)} businessId={businessId} saved={saved} />;
}

/** The time a part of the preview stays highlighted after a tick, in ms. */
const FLASH_MS = 1800;

/** The item the owner just ticked or unticked, for the highlight of the preview. */
type Flash = { item: FrontDeskItem; on: boolean; n: number };

function FrontDeskForm({ businessId, saved }: { businessId: string; saved: FrontDeskAccess }) {
  const { label } = useLabels();
  const words = itemWords(label);
  const update = useUpdateBusiness(businessId);
  const [draft, setDraft] = useState<FrontDeskAccess>(saved);
  const [flash, setFlash] = useState<Flash | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => () => void (timer.current && clearTimeout(timer.current)), []);

  const change = changedFrontDeskItems(saved, draft);
  const changed = [...change.turnedOn, ...change.turnedOff];

  function set(item: FrontDeskItem, on: boolean) {
    setDraft((current) => frontDeskAccessOf({ ...current, [item]: on }));
    setFlash((current) => ({ item, on, n: (current?.n ?? 0) + 1 }));
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => setFlash(null), FLASH_MS);
  }

  function save() {
    if (changed.length === 0) return;
    const frontDesk = Object.fromEntries(changed.map((item) => [item, draft[item]])) as Partial<FrontDeskAccess>;
    update.mutate({ settings: { frontDesk } }, { onSuccess: () => showToast("Front desk saved") });
  }

  const row = (item: FrontDeskItem) => (
    <div key={item} className="py-1">
      <Checkbox
        label={words[item].title}
        hint={words[item].hint}
        checked={draft[item]}
        disabled={item === "classesEdit" && !draft.classes}
        onChange={(next) => set(item, next)}
      />
    </div>
  );

  return (
    <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,460px)] lg:items-start">
      <div className="flex min-w-0 flex-col gap-4">
        <FormSection
          title="Pages"
          description="The front desk always opens Home, Inbox, the Calendar and the people pages. Tick more pages to give them to the front desk."
        >
          <FixedRow title="Calendar" hint="Always on. The front desk books and checks people in here." />
          <FixedRow
            title={label("member", { plural: true, capital: true })}
            hint={`Always on. The front desk opens each ${label("member")}, sees the sessions left and records payments.`}
          />
          {frontDeskPageItems.map(row)}
        </FormSection>
        <FormSection
          title={`Jobs on a ${label("member")}`}
          description={`The front desk always books, records payments, checks people in and approves new ${label("member", { plural: true })}. Tick a job to give it to the front desk too.`}
        >
          {frontDeskClientItems.map(row)}
        </FormSection>
        <SaveBar note={changed.length === 0 ? "No changes yet." : undefined}>
          <Button disabled={changed.length === 0} loading={update.isPending} onClick={save}>
            Save front desk
          </Button>
          {changed.length > 0 ? (
            <Button variant="secondary" onClick={() => setDraft(saved)}>
              Undo changes
            </Button>
          ) : null}
        </SaveBar>
        {update.error ? <InlineError error={update.error} /> : null}
      </div>
      <div className="lg:sticky lg:top-4">
        <FrontDeskPreview access={draft} flash={flash} words={words} />
      </div>
    </div>
  );
}

/** A page the front desk always has: a ticked box that does not change, with its line. */
function FixedRow({ title, hint }: { title: string; hint: string }) {
  return (
    <div className="py-1">
      <Checkbox label={title} hint={hint} checked disabled onChange={() => undefined} />
    </div>
  );
}

// --- the preview --------------------------------------------------------------------------------

/**
 * What the front desk sees with these ticks: its menu and its screens, drawn from the real menu and
 * the real buttons with sample data. A picture only (`inert`): nothing in it can be pressed. A line
 * for a screen reader says the pages and the jobs of the front desk, and changes with each tick.
 *
 * `flash`: the item ticked or unticked last. A part that came has a green glow for a moment; a part
 * that went stays for a moment, crossed out with a red line, then goes.
 */
export function FrontDeskPreview({ access, flash, words }: { access: FrontDeskAccess; flash: Flash | null; words: Words }) {
  const { label } = useLabels();
  const { forChildren } = useShape();
  const leaving = flash && !flash.on ? flash.item : null;
  const shown = useMemo(() => (leaving ? { ...access, [leaving]: true } : access), [access, leaving]);

  // The menu of the front desk, with the row of an item that just went still in it for a moment.
  const groups = ownerNavGroups({ label, unread: 0, forChildren, frontDesk: true, frontDeskAccess: shown });
  const pageOf = (item: ShellNavItem) =>
    (Object.entries(FRONT_DESK_ITEM_PAGES).find(([, page]) => page === item.to)?.[0] as FrontDeskItem | undefined) ?? null;
  const rowClassName = (item: ShellNavItem) => flashClass(pageOf(item), flash);

  const member = label("member");
  const Members = label("member", { plural: true, capital: true });
  const on = frontDeskItems.filter((item) => access[item]).map((item) => words[item].title);
  const part = (item: FrontDeskItem, children: ReactNode) =>
    shown[item] ? (
      <span key={`${item}-${flash?.item === item ? flash.n : 0}`} data-item={item} className={["inline-flex rounded-xl", flashClass(item, flash)].join(" ")}>
        {children}
      </span>
    ) : null;

  return (
    <section aria-labelledby="front-desk-preview-title" className="rounded-card border border-line bg-surface p-4 shadow-card">
      <h2 id="front-desk-preview-title" className="text-[17px]">
        What the front desk sees
      </h2>
      <p className="mt-1 text-[13px] text-ink-2">A preview with sample data. It changes when you tick a box.</p>
      <p className="sr-only" aria-live="polite" data-testid="front-desk-preview-summary">
        {on.length ? `The front desk also has: ${on.join(", ")}.` : "The front desk has only the fixed pages and jobs."}
      </p>
      <div inert className="mt-3 grid gap-3 sm:grid-cols-[168px_minmax(0,1fr)]" data-testid="front-desk-preview">
        <div className="rounded-xl border border-line bg-paper p-2" data-testid="front-desk-preview-menu">
          <p className="px-3 pb-1 text-[12px] font-semibold text-ink-3">Front desk</p>
          <ShellNavPreview groups={groups} activeTo={`/owner/parents`} rowClassName={rowClassName} />
        </div>
        <div className="flex min-w-0 flex-col gap-3">
          {shown.homeNumbers ? (
            <div key={`home-${flash?.item === "homeNumbers" ? flash.n : 0}`} data-item="homeNumbers" className={["grid grid-cols-2 gap-2 rounded-xl", flashClass("homeNumbers", flash)].join(" ")}>
              <StatTile label="Paid this week" value="AED 4,200" />
              <StatTile label="Renewals" value={3} />
            </div>
          ) : null}
          <div className="rounded-xl border border-line bg-paper p-3">
            <p className="text-[12px] font-semibold text-ink-3">{Members}</p>
            <div className="mt-2 flex flex-wrap gap-1.5">
              {part("addClients", <Button size="sm">Add {label("member", { plural: true })}</Button>)}
              {part("sendInvites", <Button size="sm" variant="secondary">Send invites</Button>)}
              {part("messageClients", <Button size="sm" variant="secondary">Message</Button>)}
              {part("exportClients", <Button size="sm" variant="secondary">Export</Button>)}
              {!shown.addClients && !shown.sendInvites && !shown.messageClients && !shown.exportClients ? (
                <span className="text-[13px] text-ink-2">The list, with no buttons.</span>
              ) : null}
            </div>
          </div>
          <div className="rounded-xl border border-line bg-paper p-3">
            <div className="flex items-center gap-2.5">
              <Avatar name="Maya Haddad" photoUrl={null} size={36} />
              <div className="min-w-0 flex-1">
                <p className="truncate text-[15px] font-semibold text-ink">Maya Haddad</p>
                <p className="text-[12px] text-ink-2">6 sessions left</p>
              </div>
              {part("photo", <Button size="sm" variant="secondary">Add a photo</Button>)}
            </div>
            <div className="mt-2 flex gap-3 border-b border-line text-[13px] font-semibold">
              <span className="border-b-2 border-accent pb-1.5 text-accent-strong">{label("member", { capital: true })}</span>
              <span className="pb-1.5 text-ink-2">Money</span>
              {part("editClient", <span className="pb-1.5 text-ink-2">Settings</span>)}
            </div>
            <div className="mt-2 flex flex-wrap gap-1.5">
              <Button size="sm" variant="secondary">
                Record payment
              </Button>
              {part("adjustBalance", <Button size="sm" variant="secondary">Add or remove sessions</Button>)}
              {part("freeze", <Button size="sm" variant="secondary">Freeze</Button>)}
              {part("paymentRequests", <Button size="sm" variant="secondary">Ask for an amount</Button>)}
            </div>
          </div>
          {shown.classes ? (
            <div key={`classes-${flash?.item === "classes" ? flash.n : 0}`} data-item="classes" className={["rounded-xl border border-line bg-paper p-3", flashClass("classes", flash)].join(" ")}>
              <div className="flex items-center justify-between gap-2">
                <p className="text-[12px] font-semibold text-ink-3">{label("group", { plural: true, capital: true })}</p>
                {part("classesEdit", <Button size="sm">Add a {label("group")}</Button>)}
              </div>
              <p className="mt-1.5 text-[13px] text-ink">Mat flow · Mon, Wed 7:00</p>
              <p className="text-[13px] text-ink">Reformer basics · Tue, Thu 18:30</p>
            </div>
          ) : null}
          <p className="text-[12px] text-ink-2">Sample {member}. Your real {label("member", { plural: true })} do not show here.</p>
        </div>
      </div>
    </section>
  );
}

/** The highlight of a part of the preview: a green glow when its item came, a red line when it went. */
function flashClass(item: FrontDeskItem | null, flash: Flash | null): string {
  if (!item || !flash || flash.item !== item) return "";
  return flash.on
    ? "outline-2 outline-offset-2 outline-confirmed motion-safe:animate-[card-arrive_1.6s_ease-out]"
    : "line-through opacity-60 outline-2 outline-offset-2 outline-dashed outline-cancelled";
}
